import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { BarChart3, Brain, Zap, Mail, Lock, AlertCircle, ArrowRight } from 'lucide-react';
import BRAND from '../brand';
import { useAuth } from '../contexts/AuthContext';

// Inline SVG icons for OAuth providers (no external deps needed)
const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const AppleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
);

const LoginPage = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState('');
  const [providers, setProviders] = useState({ google: true, apple: true }); // Default visible, API confirms
  const { login, loginWithGoogle, loginWithApple } = useAuth();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch which OAuth providers are enabled
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/providers`);
        if (res.ok) setProviders(await res.json());
      } catch (e) { console.warn('Provider check failed:', e); }
    };
    fetchProviders();
  }, [BACKEND_URL]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(username, password);
    if (!result.success) {
      setError(result.error);
      setLoading(false);
    } else {
      navigate('/');
    }
  };

  // --- Google Sign-In via Google Identity Services ---
  const handleGoogleSignIn = useCallback(async () => {
    setError('');
    setOauthLoading('google');
    try {
      // Dynamically load Google Identity Services if not already loaded
      if (!window.google?.accounts) {
        await new Promise((resolve, reject) => {
          const s = document.createElement('script');
          s.src = 'https://accounts.google.com/gsi/client';
          s.async = true;
          s.onload = resolve;
          s.onerror = () => reject(new Error('Failed to load Google Sign-In'));
          document.head.appendChild(s);
        });
      }
      const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
      if (!googleClientId) {
        setError('Google Sign-In is not configured');
        setOauthLoading('');
        return;
      }
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: async (response) => {
          const result = await loginWithGoogle(response.credential);
          if (!result.success) {
            setError(result.error);
          } else {
            navigate('/');
          }
          setOauthLoading('');
        },
      });
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          // Fallback: use the button-style popup
          const btn = document.createElement('div');
          btn.id = 'g_id_onload_tmp';
          btn.style.display = 'none';
          document.body.appendChild(btn);
          window.google.accounts.id.renderButton(btn, { type: 'icon', size: 'large' });
          btn.querySelector('div[role="button"]')?.click();
          setTimeout(() => btn.remove(), 100);
          setOauthLoading('');
        }
      });
    } catch (err) {
      setError('Google Sign-In unavailable. Please try again.');
      setOauthLoading('');
    }
  }, [loginWithGoogle, navigate]);

  // --- Apple Sign-In via Apple JS SDK ---
  const handleAppleSignIn = useCallback(async () => {
    setError('');
    setOauthLoading('apple');
    try {
      if (!window.AppleID) {
        await new Promise((resolve, reject) => {
          const s = document.createElement('script');
          s.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
          s.async = true;
          s.onload = resolve;
          s.onerror = () => reject(new Error('Failed to load Apple Sign-In'));
          document.head.appendChild(s);
        });
      }
      const appleClientId = process.env.REACT_APP_APPLE_CLIENT_ID;
      const redirectUri = window.location.origin;
      if (!appleClientId) {
        setError('Apple Sign-In is not configured');
        setOauthLoading('');
        return;
      }
      window.AppleID.auth.init({
        clientId: appleClientId,
        scope: 'name email',
        redirectURI: redirectUri,
        usePopup: true,
      });
      const response = await window.AppleID.auth.signIn();
      const idToken = response.authorization?.id_token;
      const fullName = response.user
        ? `${response.user.name?.firstName || ''} ${response.user.name?.lastName || ''}`.trim()
        : '';
      if (idToken) {
        const result = await loginWithApple(idToken, fullName);
        if (!result.success) {
          setError(result.error);
        } else {
          navigate('/');
        }
      } else {
        setError('Apple Sign-In failed');
      }
    } catch (err) {
      if (err?.error !== 'popup_closed_by_user') {
        setError('Apple Sign-In unavailable. Please try again.');
      }
    }
    setOauthLoading('');
  }, [loginWithApple, navigate]);

  const hasOAuth = providers.google || providers.apple;

  return (
    <div className="flex flex-col md:flex-row min-h-screen">
      {/* Left Panel — Brand */}
      <div className="hidden md:flex md:w-1/2 bg-gray-900 relative overflow-hidden flex-col justify-between p-12">
        {/* Background gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-orange-600/20 via-transparent to-purple-600/10" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-orange-500/5 rounded-full blur-3xl" />

        <div className="relative z-10">
          {/* Logo & Brand */}
          <div className="flex items-center space-x-4 mb-16">
            <img
              src={BRAND.logo}
              alt="Era Digital"
              className="w-14 h-14 rounded-full object-cover"
              style={{ boxShadow: `0 4px 20px ${BRAND.colors.orangeGlow}` }}
            />
            <div>
              <h1 className="text-2xl font-bold text-white">
                Era
              </h1>
              <p className="text-gray-400 text-sm">{BRAND.tagline}</p>
            </div>
          </div>

          {/* Headline */}
          <h2 className="text-4xl font-bold text-white leading-tight mb-4">
            Your Command Center for
            <br />
            <span className="bg-gradient-to-r from-orange-400 to-orange-500 bg-clip-text text-transparent">
              Market Intelligence
            </span>
          </h2>
          <p className="text-gray-400 text-lg mb-12 max-w-md">
            Real-time data, AI-powered insights — all in one platform.
          </p>

          {/* Feature Cards */}
          <div className="space-y-4">
            <div className="flex items-center space-x-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
              <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <BarChart3 className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className="text-white font-medium text-sm">Real-Time Market Data</p>
                <p className="text-gray-400 text-xs">Crypto, equities, commodities & macro</p>
              </div>
            </div>

            <div className="flex items-center space-x-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Brain className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-white font-medium text-sm">AI-Powered Analysis</p>
                <p className="text-gray-400 text-xs">Daily briefings & technical insights</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="relative z-10 text-gray-500 text-xs">
          &copy; 2026 Era Digital Limited. All rights reserved.
        </p>
      </div>

      {/* Right Panel — Form */}
      <div className="flex-1 flex items-center justify-center bg-white p-6 md:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="flex md:hidden items-center justify-center space-x-3 mb-10">
            <img
              src={BRAND.logo}
              alt="Era Digital"
              className="w-12 h-12 rounded-full object-cover"
              style={{ boxShadow: `0 4px 16px ${BRAND.colors.orangeGlow}` }}
            />
            <h1 className="text-2xl font-bold text-gray-900">
              Era
            </h1>
          </div>

          {/* Heading */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome back</h2>
            <p className="text-gray-500">Sign in to your account to continue</p>
          </div>

          {/* OAuth Buttons */}
          {hasOAuth && (
            <div className="space-y-3 mb-6">
              {providers.google && (
                <button
                  onClick={handleGoogleSignIn}
                  disabled={!!oauthLoading}
                  className="w-full h-12 flex items-center justify-center space-x-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 active:scale-[0.99] transition-all duration-150 shadow-sm disabled:opacity-60"
                >
                  {oauthLoading === 'google' ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-400 border-t-transparent" />
                  ) : (
                    <>
                      <GoogleIcon />
                      <span>Continue with Google</span>
                    </>
                  )}
                </button>
              )}
              {providers.apple && (
                <button
                  onClick={handleAppleSignIn}
                  disabled={!!oauthLoading}
                  className="w-full h-12 flex items-center justify-center space-x-3 bg-black rounded-xl text-sm font-medium text-white hover:bg-gray-900 active:scale-[0.99] transition-all duration-150 shadow-sm disabled:opacity-60"
                >
                  {oauthLoading === 'apple' ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                  ) : (
                    <>
                      <AppleIcon />
                      <span>Continue with Apple</span>
                    </>
                  )}
                </button>
              )}

              {/* Divider */}
              <div className="relative flex items-center py-1">
                <div className="flex-grow border-t border-gray-200" />
                <span className="flex-shrink-0 mx-4 text-xs text-gray-400 font-medium uppercase tracking-wider">or</span>
                <div className="flex-grow border-t border-gray-200" />
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full h-12 pl-11 pr-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-colors"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full h-12 pl-11 pr-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-colors"
                  required
                />
              </div>
            </div>

            {/* Forgot Password */}
            <div className="text-right">
              <Link to="/reset-password" className="text-sm text-orange-600 hover:text-orange-700 font-medium">
                Forgot Password?
              </Link>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3.5 flex items-start space-x-2.5">
                <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white h-12 text-base font-semibold rounded-xl shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all duration-200"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                  Signing in...
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  Sign In
                  <ArrowRight className="w-5 h-5 ml-2" />
                </div>
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="mt-8 pt-8 border-t border-gray-100 text-center">
            <p className="text-sm text-gray-500">
              Don't have an account?{' '}
              <Link to="/register" className="text-orange-600 hover:text-orange-700 font-semibold transition-colors">
                Create Account
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
